import { LoadingButton } from "@mui/lab";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import MarketGrowthAdminApi from "../../../../../infrastructure/api/marketGrowthAdmin";
import { formatMoney } from "../../../partner-commission/utils/formatMoney";
import MarketGrowthPageShell from "../components/MarketGrowthPageShell";

const emptyForm = {
  minimumPayoutAmount: "",
  attributionExpiryDays: "",
  maximumTeamSharePercent: "",
  maximumNetworkDepth: "",
  autoCommissionGenerationEnabled: true,
  referralTrackingEnabled: true,
};

const mapSettingsToForm = (data) => ({
  minimumPayoutAmount:
    data?.minimumPayoutAmount != null ? String(data.minimumPayoutAmount) : "",
  attributionExpiryDays:
    data?.attributionExpiryDays != null ? String(data.attributionExpiryDays) : "",
  maximumTeamSharePercent:
    data?.maximumTeamSharePercent != null ? String(data.maximumTeamSharePercent) : "",
  maximumNetworkDepth:
    data?.maximumNetworkDepth != null ? String(data.maximumNetworkDepth) : "",
  autoCommissionGenerationEnabled: Boolean(
    data?.autoCommissionGenerationEnabled ?? true
  ),
  referralTrackingEnabled: Boolean(data?.referralTrackingEnabled ?? true),
});

const validateForm = (form) => {
  const minimumPayoutAmount = Number(form.minimumPayoutAmount);
  const attributionExpiryDays = Number(form.attributionExpiryDays);
  const maximumTeamSharePercent = Number(form.maximumTeamSharePercent);
  const maximumNetworkDepth = Number(form.maximumNetworkDepth);

  if (!Number.isFinite(minimumPayoutAmount) || minimumPayoutAmount <= 0) {
    return "حداقل مبلغ تسویه باید عددی مثبت باشد.";
  }
  if (!Number.isInteger(attributionExpiryDays) || attributionExpiryDays <= 0) {
    return "مدت انقضای نسبت‌دهی باید عدد صحیح مثبت باشد.";
  }
  if (
    !Number.isFinite(maximumTeamSharePercent) ||
    maximumTeamSharePercent <= 0 ||
    maximumTeamSharePercent > 100
  ) {
    return "حداکثر سهم تیم باید بین ۰ و ۱۰۰ باشد.";
  }
  if (!Number.isInteger(maximumNetworkDepth) || maximumNetworkDepth < 1) {
    return "حداکثر عمق شبکه باید حداقل ۱ باشد.";
  }
  return null;
};

const MarketGrowthSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await MarketGrowthAdminApi.getSettings();
      const { error: err, data } = MarketGrowthAdminApi.normalize(
        res,
        "بارگذاری تنظیمات ناموفق بود."
      );
      if (err) {
        setError(err);
        setLoaded(false);
        return;
      }
      setForm(mapSettingsToForm(data));
      setLoaded(true);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری تنظیمات");
      setLoaded(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    const validationError = validateForm(form);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        minimumPayoutAmount: Number(form.minimumPayoutAmount),
        attributionExpiryDays: Number(form.attributionExpiryDays),
        maximumTeamSharePercent: Number(form.maximumTeamSharePercent),
        maximumNetworkDepth: Number(form.maximumNetworkDepth),
        autoCommissionGenerationEnabled: form.autoCommissionGenerationEnabled,
        referralTrackingEnabled: form.referralTrackingEnabled,
      };
      const res = await MarketGrowthAdminApi.updateSettings(payload);
      const { error: err, data } = MarketGrowthAdminApi.normalize(
        res,
        "ذخیره تنظیمات ناموفق بود."
      );
      if (err) {
        toast.error(err);
        return;
      }
      setForm(mapSettingsToForm(data));
      toast.success("تنظیمات با موفقیت ذخیره شد.");
    } catch (e) {
      toast.error(e?.message || "خطا در ذخیره تنظیمات");
    } finally {
      setSaving(false);
    }
  };

  const previewMinimumPayout =
    Number(form.minimumPayoutAmount) > 0
      ? formatMoney(Number(form.minimumPayoutAmount))
      : null;

  return (
    <MarketGrowthPageShell
      title="تنظیمات توسعه بازار"
      description="پیکربندی عملیاتی ماژول پورسانت و رهگیری معرف."
      loading={loading}
      error={error}
      empty={false}
      onRetry={load}
    >
      {!loading && !error && loaded ? (
        <Card variant="outlined">
          <CardContent>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Alert severity="info">
                  تغییرات این بخش بر درخواست تسویه، انقضای نسبت‌دهی، سهم تیم و تولید خودکار
                  پورسانت اثر می‌گذارد.
                </Alert>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="حداقل مبلغ تسویه (ریال)"
                  value={form.minimumPayoutAmount}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, minimumPayoutAmount: e.target.value }))
                  }
                  inputProps={{ min: 1 }}
                />
                {previewMinimumPayout ? (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    معادل: {previewMinimumPayout}
                  </Typography>
                ) : null}
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="مدت انقضای نسبت‌دهی (روز)"
                  value={form.attributionExpiryDays}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, attributionExpiryDays: e.target.value }))
                  }
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="حداکثر سهم تیم (درصد)"
                  value={form.maximumTeamSharePercent}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, maximumTeamSharePercent: e.target.value }))
                  }
                  inputProps={{ min: 0.01, max: 100, step: 0.01 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="حداکثر عمق شبکه"
                  value={form.maximumNetworkDepth}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, maximumNetworkDepth: e.target.value }))
                  }
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.autoCommissionGenerationEnabled}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          autoCommissionGenerationEnabled: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="تولید خودکار پورسانت فعال باشد"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.referralTrackingEnabled}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          referralTrackingEnabled: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="رهگیری معرف فعال باشد"
                />
              </Grid>

              <Grid item xs={12}>
                <LoadingButton
                  variant="contained"
                  startIcon={<SaveIcon />}
                  loading={saving}
                  onClick={handleSave}
                >
                  ذخیره تنظیمات
                </LoadingButton>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ) : null}
    </MarketGrowthPageShell>
  );
};

export default MarketGrowthSettingsPage;
